/**
 * Minimal IndexedDB. Replaced Dexie (33KB gzipped) with the five operations
 * we actually perform.
 *
 * This file exists because our storage needs are unusually small: an
 * append-only log we read whole and fold, plus a handful of device-local
 * scratch values. We never join, never query by index, never migrate a row.
 * A query engine buys us nothing.
 *
 * The IndexedDB footguns that make wrappers worth using are all in the parts
 * we don't touch. The one that matters here:
 *
 *   A transaction auto-commits the moment its microtask queue drains, so
 *   `await`ing anything non-IDB inside one silently ends it. Every function
 *   below therefore opens a transaction, issues its requests synchronously,
 *   and only then awaits.
 */

const DB_NAME = 'agrojatra';

export const EVENTS = 'events';
export const META = 'meta';

/** Wrap one IDBRequest as a promise. */
function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** Resolve once the transaction commits — not once the last request returns.
 *  Writes are only durable at commit. */
function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

let connection: Promise<IDBDatabase> | null = null;

function createStores(database: IDBDatabase): void {
  if (!database.objectStoreNames.contains(EVENTS)) {
    // ULIDs sort by time, so iterating the primary key IS canonical log
    // order — no separate ordering index needed.
    const events = database.createObjectStore(EVENTS, { keyPath: 'id' });
    events.createIndex('studyDate', 'studyDate');
    events.createIndex('type', 'type');
  }

  if (!database.objectStoreNames.contains(META)) {
    database.createObjectStore(META, { keyPath: 'key' });
  }
}

function hasStores(database: IDBDatabase): boolean {
  return database.objectStoreNames.contains(EVENTS) && database.objectStoreNames.contains(META);
}

/** Open at a specific version, or at whatever version already exists. */
function openAt(version?: number): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request =
      version === undefined ? indexedDB.open(DB_NAME) : indexedDB.open(DB_NAME, version);

    request.onupgradeneeded = () => createStores(request.result);

    // Another tab holds the database open and is blocking the upgrade. This
    // stalls silently rather than failing, so turn it into a real error.
    request.onblocked = () => {
      reject(new Error('Database upgrade blocked — close other Agrojatra tabs and reload'));
    };

    request.onsuccess = () => {
      const database = request.result;
      // If a newer version elsewhere needs to upgrade, step aside so it can.
      database.onversionchange = () => {
        database.close();
        connection = null;
      };
      resolve(database);
    };

    request.onerror = () => reject(request.error ?? new Error('Could not open IndexedDB'));
  });
}

/**
 * Open the database, upgrading only if our stores are missing.
 *
 * We deliberately do NOT hardcode a version number. Opening with an explicit
 * version lower than the one on disk fails outright with a VersionError, and
 * disks in the wild carry versions we did not choose — Dexie, which used to
 * own this database, multiplies its declared version by ten, so its "v1" is
 * really IDB v10. Asking for whatever exists and stepping one above it only
 * when a store is missing is correct against any of them, and preserves the
 * events already stored.
 */
async function connect(): Promise<IDBDatabase> {
  const existing = await openAt();
  if (hasStores(existing)) return existing;

  const next = existing.version + 1;
  existing.close();
  return openAt(next);
}

function open(): Promise<IDBDatabase> {
  connection ??= connect().catch((error: unknown) => {
    // Don't cache a failed connection — a reload or a retry should get a
    // fresh attempt rather than the same rejection forever.
    connection = null;
    throw error;
  });
  return connection;
}

async function tx(
  stores: string | string[],
  mode: IDBTransactionMode,
): Promise<IDBTransaction> {
  return (await open()).transaction(stores, mode);
}

/** Every row in a store, in primary-key order. */
export async function readAll<T>(store: string): Promise<T[]> {
  const transaction = await tx(store, 'readonly');
  return promisify(transaction.objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function readOne<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const transaction = await tx(store, 'readonly');
  return promisify(transaction.objectStore(store).get(key) as IDBRequest<T | undefined>);
}

/** Upsert one row. */
export async function writeOne(store: string, value: unknown): Promise<void> {
  const transaction = await tx(store, 'readwrite');
  transaction.objectStore(store).put(value);
  await committed(transaction);
}

/**
 * Upsert many rows in one transaction — all or nothing.
 *
 * `put` rather than `add` so replaying a sync batch that overlaps what we
 * already hold is a no-op instead of a crash. Requests are issued in a
 * synchronous loop, then awaited once, so the transaction stays open.
 */
export async function writeMany(store: string, values: readonly unknown[]): Promise<void> {
  if (values.length === 0) return;
  const transaction = await tx(store, 'readwrite');
  const objectStore = transaction.objectStore(store);
  const settled = committed(transaction);

  try {
    for (const value of values) objectStore.put(value);
  } catch (error) {
    // `put` throws synchronously on a value it cannot derive a key from.
    // Without this abort, the rows issued before it would still commit and
    // the log would hold half a batch — a state no command ever produced.
    transaction.abort();
    await settled.catch(() => undefined);
    throw error;
  }

  await settled;
}

/** Empty the given stores together. */
export async function clearAll(stores: readonly string[]): Promise<void> {
  const transaction = await tx([...stores], 'readwrite');
  for (const store of stores) transaction.objectStore(store).clear();
  await committed(transaction);
}
